import { create } from 'zustand';
import { doDELETE, doGET, doPOST, doPUT } from '../../utils/HttpUtils';
import { showError } from '../toaster';
import { Specialist, SpecialistFilters, SpecialistState } from '../../types/specialist';

const BASE = '/specialists';
const CORPORATE_BASE = '/corporates';
let gridRequestId = 0;

const store = create<SpecialistState>((set, get) => ({
    data: [],
    totalPages: 0,
    currentPage: 1,
    filters: {},
    isLoading: false,
    rows: 10,
    total: 0,
    allData: [],

    // GET /specialists (paginated, all corporates)
    fetchGrid: async () => {
        const requestId = ++gridRequestId;
        try {
            const { filters, currentPage, rows } = get();

            set({ isLoading: true });

            const { corporateId, ...queryFilters } = filters;
            const queryParams = new URLSearchParams(queryFilters);
            queryParams.append('page', String(currentPage));
            queryParams.append('limit', String(rows));
            const apiUrl = corporateId
                ? `${CORPORATE_BASE}/${corporateId}/specialists`
                : `${BASE}?${queryParams.toString()}`;

            const response = await doGET(apiUrl);
            if (requestId !== gridRequestId) return;

            if (response.status >= 200 && response.status < 400) {
                const resData = response.data?.data;
                const normalized = Array.isArray(resData) ? resData : [];
                // ponytail: corporate API has no search; filter its list here until it supports search.
                const data = corporateId
                    ? normalized.filter((specialist: Specialist) =>
                        (!filters.name || specialist.name?.toLowerCase().includes(filters.name.trim().toLowerCase())) &&
                        (!filters.specialization || specialist.specialization?.toLowerCase().includes(filters.specialization.trim().toLowerCase()))
                    )
                    : normalized;
                const pagination = response.data?.pagination;
                set({
                    data,
                    totalPages: corporateId ? 1 : pagination?.totalPages ?? 1,
                    total: corporateId ? data.length : pagination?.total ?? 0,
                });
            } else {
                showError(response.message);
            }
        } catch (err) {
            if (requestId === gridRequestId) showError('Failed to fetch specialists');
        } finally {
            if (requestId === gridRequestId) set({ isLoading: false });
        }
    },

    // GET /corporates/:id/specialists (list for one corporate, no pagination)
    fetchByCorporate: async (corporateId: string) => {
        try {
            const response = await doGET(`${CORPORATE_BASE}/${corporateId}/specialists`);
            if (response.status >= 200 && response.status < 400) {
                const resData = response.data?.data;
                const normalized = Array.isArray(resData) ? resData : [];
                set({
                    data: normalized,
                    total: normalized.length,
                    totalPages: 1,
                    currentPage: 1,
                });
                return normalized;
            } else {
                showError(response.message || 'Failed to fetch specialists for corporate');
                set({ data: [], total: 0, totalPages: 1, currentPage: 1 });
                return [];
            }
        } catch (err) {
            showError('Failed to fetch specialists for corporate');
            set({ data: [], total: 0, totalPages: 1, currentPage: 1 });
            return [];
        }
    },

    // GET /specialists/verified
    fetchVerified: async () => {
        try {
            const response = await doGET(`${BASE}/verified`);
            if (response.status >= 200 && response.status < 400) {
                return response.data?.data ?? [];
            } else {
                showError(response.message);
                return [];
            }
        } catch (err) {
            showError('Failed to fetch verified specialists');
            return [];
        }
    },

    // GET /specialists/specialization/:specialization
    fetchBySpecialization: async (specialization: string) => {
        try {
            const response = await doGET(`${BASE}/specialization/${specialization}`);
            if (response.status >= 200 && response.status < 400) {
                return response.data?.data ?? [];
            } else {
                showError(response.message);
                return [];
            }
        } catch (err) {
            showError('Failed to fetch specialists by specialization');
            return [];
        }
    },

    setFilters: (newFilters: SpecialistFilters) => {
        const scopeChanged = newFilters.corporateId !== get().filters.corporateId;
        set({
            filters: newFilters,
            currentPage: 1,
            ...(scopeChanged && { data: [], total: 0, totalPages: 1 }),
        });
        get().fetchGrid();
    },

    nextPage: () => {
        const { currentPage, totalPages, fetchGrid } = get();
        if (currentPage < totalPages) {
            set(state => ({
                currentPage: state.currentPage + 1
            }))
            fetchGrid();
        }
    },

    prevPage: () => {
        const { currentPage, fetchGrid } = get();
        if (currentPage > 1) {
            set(state => ({
                currentPage: state.currentPage - 1
            }))
            fetchGrid();
        }
    },

    onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        const { currentPage, fetchGrid, totalPages } = get();
        if (currentPage > 1 || currentPage <= totalPages) {
            set({
                currentPage: page + 1
            })
            fetchGrid();
        }
    },

    // POST /specialists
    onCreate: async (data: any) => {
        try {
            const response = await doPOST(BASE, data);
            console.log(response);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
            } else {
                showError(response.message || 'Failed to create specialist');
            }
            return response?.data?.data
        } catch (error) {
            showError('Failed to create specialist');
            return null
        }
    },

    // PUT /specialists/:id
    onUpdate: async (data: Specialist) => {
        try {
            const id = data._id;
            if (!id) {
                showError('Specialist ID is required for update');
                return null;
            }
            const response = await doPUT(`${BASE}/${id}`, data);
            console.log(response);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
            }
            return response?.data?.data
        } catch (error) {
            return null
        }
    },

    // DELETE /specialists/:id
    onDelete: async (id: string) => {
        try {
            const response = await doDELETE(`${BASE}/${id}`);
            console.log(response);
            if (response.status >= 200 && response.status < 400) {
                get().fetchGrid();
            }
        } catch (error) {

        }
    },

    // GET /specialists/:id
    detail: async (id: string) => {
        try {
            const response = await doGET(`${BASE}/${id}`);
            return response.data;
        } catch (error) {

        }
    }
}));

export const useSpecialistStore = () => store((state) => state);
